import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMoney, localIsoDate } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Checkbox, Field, Input, Select, Textarea, useToast } from '../components/ui';
import { useRefusal } from './PartnerForm';
import { IssueList, ReconciliationTables, useSetupOverview, type OpeningLine } from './FinancialSetup';

/**
 * Financial setup's forms (Finance Phase 1, 2026-10-28) — each a page: a cash or
 * bank account, the cutover day, an opening balance, confirming the opening
 * position, and reopening it.
 */

interface Currency {
  code: string;
  isBase: boolean;
  isActive: boolean;
  rateToBase: number | null;
}

const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
  void queryClient.invalidateQueries({ queryKey: ['financial-setup'] });
  void queryClient.invalidateQueries({ queryKey: ['money-accounts'] });
  void queryClient.invalidateQueries({ queryKey: ['partners'] });
};

/** Routes: `/financial-setup/accounts/new`, `/financial-setup/accounts/:id/edit`. */
export function MoneyAccountFormPage() {
  const { id } = useParams<{ id: string }>();
  const editing = !!id;
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('setup');
  const overview = useSetupOverview();
  const currencies = useQuery({ queryKey: ['currencies'], queryFn: () => api.get<Currency[]>('/currencies') }).data ?? [];
  const existing = overview.data?.accounts.find((a) => a.id === id);
  const holdsMoney = !!overview.data?.cutover?.lines.some((l) => l.moneyAccountId === id);

  const [form, setForm] = useState({ name: '', nameAr: '', type: 'BANK', currency: '', bankName: '', reference: '', notes: '', isActive: true });
  useEffect(() => {
    if (existing)
      setForm({
        name: existing.name,
        nameAr: existing.nameAr ?? '',
        type: existing.type,
        currency: existing.currency,
        bankName: existing.bankName ?? '',
        reference: existing.reference ?? '',
        notes: existing.notes ?? '',
        isActive: existing.isActive,
      });
  }, [existing?.id]);
  const currency = form.currency || currencies.find((c) => c.isBase)?.code || '';

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        nameAr: form.nameAr || (editing ? null : undefined),
        type: form.type,
        currency,
        bankName: form.type === 'BANK' ? form.bankName || (editing ? null : undefined) : undefined,
        reference: form.reference || (editing ? null : undefined),
        notes: form.notes || (editing ? null : undefined),
        ...(editing ? { isActive: form.isActive } : {}),
      };
      return editing ? api.patch(`/money-accounts/${id}`, body) : api.post('/money-accounts', body);
    },
    onSuccess: () => {
      invalidate(queryClient);
      navigate('/financial-setup');
    },
    onError: (e) => toast.error(refusal(e)),
  });

  return (
    <FormPage
      title={editing ? t('setup.editAccount') : t('setup.newAccount')}
      backTo="/financial-setup"
      backLabel={t('setup.title')}
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={!form.name.trim() || !currency}
      loading={overview.isLoading}
      error={overview.error}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('setup.accountName')} required>
          <Input value={form.name} maxLength={80} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={`${t('setup.accountName')} (AR)`}>
          <Input dir="rtl" value={form.nameAr} maxLength={80} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('setup.accountType')} required>
          <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="BANK">{t('setup.accountBANK')}</option>
            <option value="CASH">{t('setup.accountCASH')}</option>
          </Select>
        </Field>
        <Field label={t('common.currency')} required hint={editing && holdsMoney ? t('setup.accountCurrencyLocked') : undefined}>
          <Select value={currency} disabled={editing && holdsMoney} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            {currencies.filter((c) => c.isActive || c.code === currency).map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
        </Field>
        {form.type === 'BANK' && (
          <Field label={t('setup.bankName')}>
            <Input dir="auto" value={form.bankName} maxLength={80} onChange={(e) => setForm({ ...form, bankName: e.target.value })} />
          </Field>
        )}
        <Field label={t('setup.reference')} hint={t('setup.referenceHint')}>
          <Input dir="auto" value={form.reference} maxLength={60} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        </Field>
        <Field label={t('common.notes')} className="sm:col-span-2">
          <Textarea dir="auto" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
        {editing && <Checkbox label={t('setup.closeAccount')} checked={!form.isActive} onChange={(e) => setForm({ ...form, isActive: !e.target.checked })} />}
      </FormGrid>
    </FormPage>
  );
}

/** Route: `/financial-setup/cutover`. */
export function CutoverFormPage() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('setup');
  const overview = useSetupOverview();
  const cutover = overview.data?.cutover;
  const [form, setForm] = useState({ cutoverDate: '', notes: '' });
  useEffect(() => {
    if (cutover) setForm({ cutoverDate: cutover.cutoverDate, notes: cutover.notes ?? '' });
  }, [cutover?.id]);

  const save = useMutation({
    mutationFn: () => api.put('/financial-setup/cutover', { cutoverDate: form.cutoverDate, notes: form.notes || null, version: cutover?.version }),
    onSuccess: () => {
      invalidate(queryClient);
      navigate('/financial-setup');
    },
    onError: (e) => toast.error(refusal(e)),
  });

  return (
    <FormPage
      title={cutover ? t('setup.changeCutover') : t('setup.setCutover')}
      subtitle={t('setup.cutoverHint')}
      backTo="/financial-setup"
      backLabel={t('setup.title')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!form.cutoverDate}
      loading={overview.isLoading}
      error={overview.error}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('setup.cutoverDate')} required>
          <Input type="date" max={overview.data?.today ?? localIsoDate()} value={form.cutoverDate} onChange={(e) => setForm({ ...form, cutoverDate: e.target.value })} autoFocus />
        </Field>
        <Field label={t('setup.baseCurrency')} hint={t('setup.baseCurrencyHint')}>
          <p className="h-10 py-2">{overview.data?.baseCurrency}</p>
        </Field>
        <Field label={t('common.notes')} className="sm:col-span-2">
          <Textarea dir="auto" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/** Routes: `/financial-setup/opening/new`, `/financial-setup/opening/:lineId/edit`. */
export function OpeningBalanceFormPage() {
  const { lineId } = useParams<{ lineId: string }>();
  const editing = !!lineId;
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('setup');
  const overview = useSetupOverview();
  const currencies = useQuery({ queryKey: ['currencies'], queryFn: () => api.get<Currency[]>('/currencies') }).data ?? [];
  const existing: OpeningLine | undefined = overview.data?.cutover?.lines.find((l) => l.id === lineId);

  const [form, setForm] = useState({ kind: 'CASH', moneyAccountId: '', partnerId: '', currency: '', amount: '', exchangeRate: '', notes: '' });
  useEffect(() => {
    if (existing)
      setForm({
        kind: existing.kind,
        moneyAccountId: existing.moneyAccountId ?? '',
        partnerId: existing.partnerId ?? '',
        currency: existing.currency,
        amount: existing.amount,
        exchangeRate: existing.baseCurrency === overview.data?.baseCurrency ? existing.exchangeRate : '',
        notes: existing.notes ?? '',
      });
  }, [existing?.id]);

  const base = overview.data?.baseCurrency ?? '';
  const cash = form.kind === 'CASH';
  const account = overview.data?.accounts.find((a) => a.id === form.moneyAccountId);
  const currency = cash ? (account?.currency ?? '') : form.currency || base;
  const foreign = !!currency && currency !== base;

  const save = useMutation({
    mutationFn: () => {
      const rate = foreign ? Number(form.exchangeRate) : undefined;
      return editing
        ? api.patch(`/financial-setup/opening-balances/${lineId}`, { amount: Number(form.amount), exchangeRate: rate, notes: form.notes || null })
        : api.post('/financial-setup/opening-balances', {
            kind: form.kind,
            moneyAccountId: cash ? form.moneyAccountId : undefined,
            partnerId: cash ? undefined : form.partnerId,
            currency: cash ? undefined : currency,
            amount: Number(form.amount),
            exchangeRate: rate,
            notes: form.notes || undefined,
          });
    },
    onSuccess: () => {
      invalidate(queryClient);
      navigate('/financial-setup');
    },
    onError: (e) => toast.error(refusal(e)),
  });

  const ready =
    form.amount !== '' &&
    Number(form.amount) >= 0 &&
    (cash ? !!form.moneyAccountId : !!form.partnerId && !!currency) &&
    (!foreign || Number(form.exchangeRate) > 0);

  return (
    <FormPage
      title={editing ? t('setup.editLine') : t('setup.addLine')}
      subtitle={overview.data?.cutover ? formatDate(overview.data.cutover.cutoverDate, locale) : undefined}
      backTo="/financial-setup"
      backLabel={t('setup.title')}
      submitLabel={editing ? t('common.save') : t('common.add')}
      submitting={save.isPending}
      disabled={!ready}
      loading={overview.isLoading}
      error={overview.error}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('setup.lineKind')} required className="sm:col-span-2">
          <Select value={form.kind} disabled={editing} onChange={(e) => setForm({ ...form, kind: e.target.value, exchangeRate: '' })}>
            {(['CASH', 'PARTNER_LOAN_PAYABLE', 'PARTNER_ADVANCE_RECEIVABLE', 'PARTNER_EQUITY'] as const).map((k) => (
              <option key={k} value={k}>{t(`setup.kind.${k}` as TranslationKey)}</option>
            ))}
          </Select>
        </Field>
        {cash ? (
          <Field label={t('ptx.account')} required>
            <Select value={form.moneyAccountId} disabled={editing} onChange={(e) => setForm({ ...form, moneyAccountId: e.target.value, exchangeRate: '' })}>
              <option value="">—</option>
              {overview.data?.accounts.map((a) => (
                <option key={a.id} value={a.id}>{localized(a)} · {a.currency}</option>
              ))}
            </Select>
          </Field>
        ) : (
          <>
            <Field label={t('ptx.partner')} required>
              <Select value={form.partnerId} disabled={editing} onChange={(e) => setForm({ ...form, partnerId: e.target.value })}>
                <option value="">—</option>
                {overview.data?.partners.map((p) => (
                  <option key={p.id} value={p.id}>{localized(p)}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('common.currency')} required>
              <Select value={currency} disabled={editing} onChange={(e) => setForm({ ...form, currency: e.target.value, exchangeRate: '' })}>
                {currencies.filter((c) => c.isActive).map((c) => (
                  <option key={c.code} value={c.code}>{c.code}</option>
                ))}
              </Select>
            </Field>
          </>
        )}
        <Field label={`${t('common.amount')}${currency ? ` (${currency})` : ''}`} required>
          <Input type="number" min={0} step="0.01" dir="ltr" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </Field>
        {foreign && (
          <Field label={t('setup.lineRate', { base })} required hint={t('setup.lineRateHint')}>
            <Input type="number" min={0} step="0.00000001" dir="ltr" value={form.exchangeRate} onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })} />
          </Field>
        )}
        {foreign && Number(form.exchangeRate) > 0 && form.amount !== '' && (
          <Field label={t('ptx.baseValue', { base })}>
            <p className="h-10 py-2 tabular">{formatMoney(Number(form.amount) * Number(form.exchangeRate), base, locale)}</p>
          </Field>
        )}
        <Field label={t('common.notes')} className="sm:col-span-2">
          <Textarea dir="auto" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/** Route: `/financial-setup/confirm`. The reconciliation as it stands is the page's summary. */
export function CutoverConfirmPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('setup');
  const overview = useSetupOverview();
  const rec = overview.data?.reconciliation;

  const confirm = useMutation({
    mutationFn: () => api.post('/financial-setup/confirm', { version: overview.data?.cutover?.version }),
    onSuccess: () => {
      invalidate(queryClient);
      navigate('/financial-setup');
    },
    onError: (e) => {
      if (e instanceof ApiError) void overview.refetch();
      toast.error(refusal(e));
    },
  });

  return (
    <FormPage
      title={t('setup.confirm')}
      subtitle={overview.data?.cutover ? formatDate(overview.data.cutover.cutoverDate, locale) : undefined}
      backTo="/financial-setup"
      backLabel={t('setup.title')}
      submitLabel={t('setup.confirm')}
      submitting={confirm.isPending}
      disabled={!rec || rec.blocked || overview.data?.cutover?.status !== 'DRAFT'}
      loading={overview.isLoading}
      error={overview.error}
      onSubmit={() => confirm.mutate()}
      width="lg"
      summary={<p className="text-body-sm">{t('setup.confirmHint')}</p>}
    >
      {rec && (
        <div className="space-y-6">
          <div>
            <p className="mb-2 text-body-sm font-medium">{t('setup.issues')}</p>
            <IssueList issues={rec.issues} />
          </div>
          <ReconciliationTables data={rec} />
        </div>
      )}
    </FormPage>
  );
}

/** Route: `/financial-setup/reopen`. Shows what is confirmed now — what reopening puts back in play. */
export function CutoverReopenPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('setup');
  const overview = useSetupOverview();
  const [reason, setReason] = useState('');
  const rec = overview.data?.reconciliation;

  const reopen = useMutation({
    mutationFn: () => api.post('/financial-setup/reopen', { version: overview.data?.cutover?.version, reason }),
    onSuccess: () => {
      invalidate(queryClient);
      navigate('/financial-setup');
    },
    onError: (e) => toast.error(refusal(e)),
  });

  return (
    <FormPage
      title={t('setup.reopenTitle')}
      subtitle={overview.data?.cutover ? formatDate(overview.data.cutover.cutoverDate, locale) : undefined}
      backTo="/financial-setup"
      backLabel={t('setup.title')}
      submitLabel={t('setup.reopen')}
      destructive
      submitting={reopen.isPending}
      disabled={!reason.trim() || overview.data?.cutover?.status !== 'CONFIRMED'}
      loading={overview.isLoading}
      error={overview.error}
      onSubmit={() => reopen.mutate()}
      width="lg"
      summary={<p className="text-body-sm">{t('setup.reopenHint')}</p>}
    >
      <Field label={t('common.reason')} required>
        <Textarea dir="auto" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
      {rec && (
        <div className="mt-6">
          <ReconciliationTables data={rec} />
        </div>
      )}
    </FormPage>
  );
}
